import React, { useEffect, useState } from 'react';
import './ShoppingRefinementBar.scss';

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

// Module-level cache — the taxonomy tree is static/derived server data (see
// server/shared/shoppingTaxonomy.js's getShoppingTaxonomyTree), so every
// SearchResults mount reuses the same fetch instead of re-requesting it.
let taxonomyPromise = null;
function fetchShoppingTaxonomy() {
  if (!taxonomyPromise) {
    taxonomyPromise = fetch(`${API_BASE}/api/search/shopping-taxonomy`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return taxonomyPromise;
}

// Emoji are a pure presentation/navigation aid on the two top levels only
// (per explicit product direction) — cosmetic prefixes keyed by the same
// stable facet VALUES the backend taxonomy tree already returns, not a
// second copy of the taxonomy itself. Every real label/option still comes
// from getShoppingTaxonomyTree(); this only decorates level 1/2 chips.
// Product types (level 3) deliberately get no emoji.
const AUDIENCE_EMOJI = { women: '👩', men: '👨', kids: '🧒', unisex: '' };
const DEPARTMENT_EMOJI = { clothing: '👕', shoes: '👟', bags: '👜', accessories: '⌚', beauty: '✨' };

function Chip({ label, emoji, isActive, onClick }) {
  return (
    <button
      type="button"
      className={`shopping-refine__chip ${isActive ? 'is-active' : ''}`}
      aria-pressed={isActive}
      onClick={onClick}
    >
      {emoji ? <span className="shopping-refine__chip-emoji" aria-hidden="true">{emoji}</span> : null}
      {label}
    </button>
  );
}

function hasOptions(tree, audience, department) {
  if (!tree) return false;
  const opts = audience
    ? tree.categoriesByAudienceDepartment?.[audience]?.[department]
    : tree.categoriesByDepartment?.[department];
  return Array.isArray(opts) && opts.length > 0;
}

function isCategoryValid(tree, audience, department, shoppingCategory) {
  if (!shoppingCategory) return true;
  if (!tree || !department) return false;
  const opts = audience
    ? tree.categoriesByAudienceDepartment?.[audience]?.[department]
    : tree.categoriesByDepartment?.[department];
  return Array.isArray(opts) && opts.some((o) => o.value === shoppingCategory);
}

// SearchResults' fashion browse refinement (2026-09, production UX pass).
// Left-aligned label-above-chips rows, no card/container around the whole
// thing. Level 3 (product type) shows every real option at once in a
// natural wrap-grid — no horizontal scroll, no "Ще" overflow. The type list
// (and, once an audience is chosen, the department row itself) is scoped to
// exactly what CreateListing's real dropdown state machine allows for that
// audience — server/shared/shoppingTaxonomy.js's categoriesByAudienceDepartment,
// derived by walking the actual fashionFields/beautyFields/accessoriesFields/
// kidsFields functions, never a hand-maintained "men's categories" list.
// Changing audience/department auto-clears any selection that's no longer
// reachable, so the URL can never describe an impossible combination. All
// state changes go through `onChange` (a URL push in SearchResults.js) —
// never local-only state.
export default function ShoppingRefinementBar({ audience, department, shoppingCategory, onChange }) {
  const [tree, setTree] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchShoppingTaxonomy().then((data) => {
      if (alive) setTree(data);
    });
    return () => {
      alive = false;
    };
  }, []);

  const inShoppingContext = !!(audience || department || shoppingCategory);
  if (!inShoppingContext || !tree) return null;

  const visibleDepartments = audience
    ? tree.departments.filter((d) => hasOptions(tree, audience, d.value))
    : tree.departments;

  const categoryOptions = department
    ? (audience
        ? tree.categoriesByAudienceDepartment?.[audience]?.[department]
        : tree.categoriesByDepartment?.[department]) || []
    : [];

  const handleAudienceClick = (value) => {
    const nextAudience = audience === value ? null : value;
    const patch = { audience: nextAudience };

    // No impossible state: if the current department has zero real options
    // under the new audience, it (and any chosen type) gets cleared too;
    // otherwise just re-check the currently chosen type still applies.
    if (department && !hasOptions(tree, nextAudience, department)) {
      patch.department = null;
      patch.shoppingCategory = null;
    } else if (!isCategoryValid(tree, nextAudience, department, shoppingCategory)) {
      patch.shoppingCategory = null;
    }

    onChange(patch);
  };

  const handleDepartmentClick = (value) => {
    const nextDepartment = department === value ? null : value;
    // Changing/clearing department always invalidates any chosen type — it
    // belongs to the OLD department's option list.
    onChange({ department: nextDepartment, shoppingCategory: null });
  };

  return (
    <div className="shopping-refine">
      <div className="shopping-refine__section">
        <span className="shopping-refine__label">Для кого?</span>
        <div className="shopping-refine__chips">
          {tree.audiences.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              emoji={AUDIENCE_EMOJI[opt.value]}
              isActive={audience === opt.value}
              onClick={() => handleAudienceClick(opt.value)}
            />
          ))}
        </div>
      </div>

      <div className="shopping-refine__section">
        <span className="shopping-refine__label">Що шукаємо?</span>
        <div className="shopping-refine__chips">
          {visibleDepartments.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              emoji={DEPARTMENT_EMOJI[opt.value]}
              isActive={department === opt.value}
              onClick={() => handleDepartmentClick(opt.value)}
            />
          ))}
        </div>
      </div>

      {department && categoryOptions.length > 0 && (
        <div className="shopping-refine__section shopping-refine__section--types" key={`${audience || 'any'}-${department}`}>
          <span className="shopping-refine__label">Тип</span>
          <div className="shopping-refine__chips shopping-refine__chips--wrap">
            {categoryOptions.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                isActive={shoppingCategory === opt.value}
                onClick={() => onChange({ shoppingCategory: shoppingCategory === opt.value ? null : opt.value })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
